<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { isSessionProvider } from 'dashboard/helper/whatsappSession';
import { isProviderOffline } from 'dashboard/helper/whatsapp';
import {
  HYBRID_QR_SOURCE,
  hybridQrConnection,
  isHybridQrDown,
} from 'dashboard/helper/whatsappHybrid';
import Banner from 'dashboard/components-next/banner/Banner.vue';

const { t } = useI18n();
const router = useRouter();
const { isAdmin } = useAdmin();
const { accountId } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');

// Same test as the pink banner in the inbox settings (isProviderOffline), so the two never
// disagree and an inbox that was already down before this banner existed shows too. It
// also stays quiet for a pairing in progress and for an inbox that never reported a
// connection, which isProviderOffline alone would flag.
const isDisconnected = inbox => {
  const connection = inbox.provider_connection?.connection;
  return (
    inbox.channel_type === INBOX_TYPES.WHATSAPP &&
    isSessionProvider(inbox.provider) &&
    !!connection &&
    connection !== 'connecting' &&
    isProviderOffline(inbox.provider_connection)
  );
};

const disconnectedInboxes = computed(() =>
  inboxes.value.filter(isDisconnected)
);

// The QR connection behind a hybrid official inbox. One linked to a QR inbox is that inbox's own
// session, which the banner above already names, so it is not announced twice.
const hybridDownInboxes = computed(() =>
  inboxes.value.filter(
    inbox =>
      inbox.channel_type === INBOX_TYPES.WHATSAPP &&
      isHybridQrDown(inbox, inboxes.value) &&
      hybridQrConnection(inbox, inboxes.value).source !==
        HYBRID_QR_SOURCE.LINKED
  )
);

const hybridMessage = computed(() => {
  const [first] = hybridDownInboxes.value;
  const base =
    hybridDownInboxes.value.length === 1
      ? t('WHATSAPP_DISCONNECTED_BANNER.HYBRID_ONE', { name: first.name })
      : t('WHATSAPP_DISCONNECTED_BANNER.HYBRID_MANY', {
          count: hybridDownInboxes.value.length,
        });
  return isAdmin.value
    ? base
    : `${base} ${t('WHATSAPP_DISCONNECTED_BANNER.CONTACT_ADMIN')}`;
});

const goToHybridConnect = () => {
  const [first] = hybridDownInboxes.value;
  router.push(
    hybridDownInboxes.value.length === 1
      ? {
          name: 'settings_inbox_show',
          params: {
            accountId: accountId.value,
            inboxId: first.id,
            tab: 'configuration',
          },
        }
      : { name: 'settings_inbox_list', params: { accountId: accountId.value } }
  );
};

const message = computed(() => {
  const [first] = disconnectedInboxes.value;
  const base =
    disconnectedInboxes.value.length === 1
      ? t('WHATSAPP_DISCONNECTED_BANNER.ONE', { name: first.name })
      : t('WHATSAPP_DISCONNECTED_BANNER.MANY', {
          count: disconnectedInboxes.value.length,
        });
  return isAdmin.value
    ? base
    : `${base} ${t('WHATSAPP_DISCONNECTED_BANNER.CONTACT_ADMIN')}`;
});

const actionLabel = computed(() =>
  disconnectedInboxes.value.length === 1
    ? t('WHATSAPP_DISCONNECTED_BANNER.CONNECT')
    : t('WHATSAPP_DISCONNECTED_BANNER.CONNECT_MANY')
);

const goToConnect = () => {
  const [first] = disconnectedInboxes.value;
  router.push(
    disconnectedInboxes.value.length === 1
      ? {
          name: 'settings_inbox_show',
          params: { accountId: accountId.value, inboxId: first.id },
          query: { conectar: 1 },
        }
      : { name: 'settings_inbox_list', params: { accountId: accountId.value } }
  );
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Banner
    v-if="disconnectedInboxes.length"
    color="ruby"
    class="!rounded-none !justify-center"
    data-test-id="whatsapp-disconnected-banner"
    :action-label="isAdmin ? actionLabel : null"
    @action="goToConnect"
  >
    <span class="text-xs">{{ message }}</span>
  </Banner>
  <Banner
    v-if="hybridDownInboxes.length"
    color="ruby"
    class="!rounded-none !justify-center"
    data-test-id="hybrid-qr-disconnected-banner"
    :action-label="isAdmin ? t('WHATSAPP_DISCONNECTED_BANNER.RECONNECT') : null"
    @action="goToHybridConnect"
  >
    <span class="text-xs">{{ hybridMessage }}</span>
  </Banner>
</template>
